import { heicTo } from "heic-to";
import { getUploadUrl, saveDocumentInfo, uploadFileToS3 } from "../../../libraries/api";

const convertHeicToJpeg = async (file) => {
  try {
    const blob = await heicTo({
      blob: file,
      type: "image/jpeg",
      quality: 1,
    });
    return new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
      type: "image/jpeg",
    });
  } catch (error) {
    console.error("HEIC conversion error:", error);
    return null;
  }
};

const blobFromCanvas = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));
const convertWebPToJpeg = async (file) => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(img, 0, 0);

    const blob = await blobFromCanvas(canvas, "image/jpeg", 1);
    if (!blob) return null;

    return new File([blob], file.name.replace(/\.webp$/i, ".jpg"), { type: "image/jpeg" });
  } catch (e) {
    console.error(`Conversion failed: could not create JPEG from "${file.name}"`);
    return null;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export const normaliseDocument = async (file, setIsConverting) => {
  const lowerName = file.name.toLowerCase();

  if (lowerName.endsWith(".heic") || lowerName.endsWith(".heif") || lowerName.endsWith(".webp")) {
    setIsConverting(true);
    try {
      if (lowerName.endsWith(".heic") || lowerName.endsWith(".heif")) {
        return await convertHeicToJpeg(file);
      }
      if (lowerName.endsWith(".webp")) {
        return await convertWebPToJpeg(file);
      }
    } catch {
      return null;
    } finally {
      setIsConverting(false);
    }
  }

  return file;
};

export const uploadDocument = async (file, fileName, itin, folderId, travellerId, restrictedToTravellerIds = [], setCachedUploadData, progressCallback) => {
  try {
    const getUploadUrlBody = {
      contentType: file.type,
    }

    const { res, data, error } = await getUploadUrl(itin.operatorCode, itin.passcode, getUploadUrlBody);
    if (!res.ok || error) {
      const msg = error || data?.error?.[0]?.message || data?.error ||"Get upload URL error";
      throw new Error(msg);
    }

    progressCallback?.(2, 0);

    await uploadFileToS3(data.url, file, ({ realPercent, loaded }) => {
      // Scale 0–100% S3 to 2–97% global
      const scaledPercent = 2 + realPercent * 0.95;
      const percent = Math.min(97, Math.round(scaledPercent));
      progressCallback?.(percent, loaded);
    });


    const saveDocumentInfoBody = {
      folderId,
      fileUrl: data.s3url,
      restrictedToTravellerIds,
      name: fileName,
    }
    setCachedUploadData({ s3url: data.s3url, fileName });

    const { res: saveDocumentInfoRes, data: saveDocumentInfoData, error: saveDocumentInfoError } =  await saveDocumentInfo(itin.operatorCode, itin.passcode, saveDocumentInfoBody, travellerId);
    if (!saveDocumentInfoRes.ok || saveDocumentInfoError) {
      const msg = saveDocumentInfoError || saveDocumentInfoData?.error?.[0]?.message || saveDocumentInfoData?.error ||"Save document info error";
      throw new Error(msg);
    }
    progressCallback?.(99, file.size);
    return saveDocumentInfoData;
  } catch (err) {
    // eslint-disable-next-line no-throw-literal
    throw { modalMessage: err?.message ?? "Unexpected upload error" };
  }
};